// VideoTrimmer.tsx - FFmpeg version with better error handling
import { useState, useRef, useEffect, FC } from "react";
import Swal from "sweetalert2";
import { VideoPlayer, VideoPlayerRef } from "./VideoPlayer";
import { Timeline } from "./TimeLine";
import { VideoControls } from "./VideoControls";
import Paragraph from "../../../components/core/typography/Paragraph";
import { CgClose } from "react-icons/cg";

interface VideoTrimmerProps {
  videoFile: File;
  videoUrl: string;
  maxDuration: number;
  onTrimComplete: (trimmedFile: File) => void;
  onCancel: () => void;
}

export const VideoTrimmer: FC<VideoTrimmerProps> = ({
  videoFile,
  videoUrl,
  maxDuration,
  onTrimComplete,
  onCancel,
}) => {
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [loadingFFmpeg, setLoadingFFmpeg] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const videoRef = useRef<VideoPlayerRef>(null);
  const ffmpegRef = useRef<any>(null);

  const clampedCurrentTime = Math.max(
    trimStart,
    Math.min(currentTime, trimEnd)
  );

  useEffect(() => {
    if (duration > 0) {
      setTrimEnd(Math.min(duration, maxDuration));
    }
  }, [duration, maxDuration]);

  useEffect(() => {
    const loadFFmpeg = async () => {
      try {
        console.log("[FFmpeg] Starting to load...");

        // Dynamic import to avoid build issues
        const { FFmpeg } = await import("@ffmpeg/ffmpeg");
        const { fetchFile } = await import("@ffmpeg/util");

        const ffmpeg = new FFmpeg();

        ffmpeg.on("log", ({ message }: any) => {
          console.log("[FFmpeg Log]", message);
        });

        ffmpeg.on("progress", ({ progress }: any) => {
          const percent = Math.round(progress * 100);
          setExportProgress(percent);
          console.log("[FFmpeg Progress]", percent + "%");
        });

        // Load FFmpeg with CDN URLs
        await ffmpeg.load({
          coreURL:
            "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.js",
          wasmURL:
            "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.wasm",
        });

        // Store fetchFile in ref for later use
        (ffmpeg as any).fetchFile = fetchFile;

        ffmpegRef.current = ffmpeg;
        setFfmpegLoaded(true);
        setLoadingFFmpeg(false);
        console.log("[FFmpeg] Successfully loaded!");
      } catch (error: any) {
        console.error("[FFmpeg] Load error:", error);
        setLoadError(error.message || "Failed to load FFmpeg");
        setLoadingFFmpeg(false);

        Swal.fire({
          icon: "error",
          title: "Failed to Load Video Processor",
          html: `
            <p>Error: ${error.message || "Unknown error"}</p>
            <p class="text-sm mt-2">Please make sure you have installed:</p>
            <code class="text-xs">npm install @ffmpeg/ffmpeg @ffmpeg/util</code>
          `,
          confirmButtonColor: "#FFD45A",
        });
      }
    };

    loadFFmpeg();
  }, []);

  const handleLoadedMetadata = (dur: number) => {
    setDuration(dur);
    setTrimEnd(Math.min(dur, maxDuration));
  };

  const handleTimeUpdate = (time: number) => {
    setCurrentTime(time);
    if (isPlaying && time >= trimEnd) {
      videoRef.current?.seekTo(trimStart);
    }
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      videoRef.current?.pause();
    } else {
      videoRef.current?.seekTo(clampedCurrentTime);
      videoRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setTrimStart(0);
    setTrimEnd(Math.min(duration, maxDuration));
    setCurrentTime(0);
    setIsPlaying(false);
    videoRef.current?.seekTo(0);
    videoRef.current?.pause();
  };

  const handleSeek = (time: number) => {
    const clampedTime = Math.max(trimStart, Math.min(time, trimEnd));
    setCurrentTime(clampedTime);
    videoRef.current?.seekTo(clampedTime);
    if (isPlaying) {
      videoRef.current?.play();
    }
  };

  const handleTrimStartChange = (time: number) => {
    setTrimStart(time);
    if (clampedCurrentTime < time) {
      handleSeek(time);
    }
  };

  const handleTrimEndChange = (time: number) => {
    setTrimEnd(time);
    if (clampedCurrentTime > time) {
      handleSeek(time);
    }
  };

  const handleExport = async () => {
    console.log("[Export] Button clicked");
    console.log("[Export] FFmpeg loaded?", ffmpegLoaded);
    console.log("[Export] FFmpeg ref:", ffmpegRef.current);

    if (trimEnd - trimStart > maxDuration) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Trimmed duration must be ${maxDuration}s or less`,
        confirmButtonColor: "#FFD45A",
      });
      return;
    }

    if (!ffmpegLoaded || !ffmpegRef.current) {
      Swal.fire({
        icon: "error",
        title: "Video Processor Not Ready",
        text: loadError || "Please wait for FFmpeg to load and try again.",
        confirmButtonColor: "#FFD45A",
      });
      return;
    }

    setIsExporting(true);
    setExportProgress(0);

    try {
      const ffmpeg = ffmpegRef.current;
      const fetchFile = (ffmpeg as any).fetchFile;

      if (!fetchFile) {
        throw new Error("fetchFile not available");
      }

      const originalType = videoFile.type || "video/mp4";
      const extension = videoFile.name.split(".").pop()?.toLowerCase() || "mp4";
      const inputFileName = `input.${extension}`;
      const outputFileName = `output.${extension}`;

      console.log("[Trim] Configuration:", {
        start: trimStart.toFixed(2),
        end: trimEnd.toFixed(2),
        duration: (trimEnd - trimStart).toFixed(2),
        originalType,
        extension,
        fileSize: (videoFile.size / 1024 / 1024).toFixed(2) + " MB",
      });

      console.log("[FFmpeg] Writing input file...");
      await ffmpeg.writeFile(inputFileName, await fetchFile(videoFile));

      console.log("[FFmpeg] Starting trim operation...");
      await ffmpeg.exec([
        "-i",
        inputFileName,
        "-ss",
        trimStart.toFixed(3),
        "-to",
        trimEnd.toFixed(3),
        "-c",
        "copy",
        "-avoid_negative_ts",
        "make_zero",
        outputFileName,
      ]);

      console.log("[FFmpeg] Reading output file...");
      const data = await ffmpeg.readFile(outputFileName);

      const blob = new Blob([data], { type: originalType });
      const originalName = videoFile.name.replace(/\.[^/.]+$/, "");
      const trimmedFile = new File(
        [blob],
        `${originalName}_trimmed.${extension}`,
        {
          type: originalType,
          lastModified: Date.now(),
        }
      );

      console.log("[FFmpeg] Cleaning up...");
      await ffmpeg.deleteFile(inputFileName);
      await ffmpeg.deleteFile(outputFileName);

      console.log("[Trim] SUCCESS:", {
        type: trimmedFile.type,
        name: trimmedFile.name,
        size: (trimmedFile.size / 1024 / 1024).toFixed(2) + " MB",
      });

      setIsExporting(false);
      setExportProgress(0);
      onTrimComplete(trimmedFile);
    } catch (error: any) {
      console.error("[Trim] ERROR:", error);
      setIsExporting(false);
      setExportProgress(0);

      Swal.fire({
        icon: "error",
        title: "Trim Error",
        html: `
          <p>${error?.message || "An error occurred while trimming."}</p>
          <p class="text-sm mt-2">Check browser console for details.</p>
        `,
        confirmButtonColor: "#FFD45A",
      });
    }
  };

  const handleCancel = () => {
    setIsPlaying(false);
    videoRef.current?.pause();
    onCancel();
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center transition-colors z-[51] bg-site-black/60">
      <div className="bg-white rounded-lg shadow transition-all md:max-h-[calc(100vh-160px)] max-h-[calc(100vh-120px)] scale-100 opacity-100 m-3 sm:p-6 p-4 flex flex-col max-w-4xl w-full overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <Paragraph text24>Trim Video</Paragraph>
          <button
            onClick={handleCancel}
            className={`absolute right-2 top-2 z-10 rounded-lg text-grey-400 bg-white hover:bg-grey-500 hover:text-grey-600`}
            disabled={isExporting}
          >
            <CgClose size={20} color="#333333" />
          </button>
        </div>

        <div className="ms:space-y-6 sm:space-y-5 space-y-4">
          <VideoPlayer
            ref={videoRef}
            videoUrl={videoUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
          />

          <VideoControls
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onReset={handleReset}
            onExport={handleExport}
          />

          <Timeline
            duration={duration}
            currentTime={clampedCurrentTime}
            trimStart={trimStart}
            trimEnd={trimEnd}
            onTrimStartChange={handleTrimStartChange}
            onTrimEndChange={handleTrimEndChange}
            onSeek={handleSeek}
          />

          {trimEnd - trimStart > maxDuration && (
            <span className="py-1 px-2.5 bg-site-red/10 border border-site-red rounded-md text-site-red text-[14px] leading-[21px] block">
              Trimmed duration ({Math.round(trimEnd - trimStart)}s) exceeds{" "}
              {maxDuration}s limit.
            </span>
          )}

          {loadingFFmpeg && !ffmpegLoaded && !loadError && (
            <div className="p-3 bg-light-yellow border border-site-yellow rounded-md text-site-black text-center">
              <p className="font-semibold">⏳ Loading video processor...</p>
              <p className="text-sm mt-1">
                This may take 10-15 seconds (one-time only)
              </p>
            </div>
          )}

          {loadError && (
            <div className="p-3 bg-site-red/10 border border-site-red rounded-md text-site-red text-center">
              <p className="font-semibold">❌ Failed to load FFmpeg</p>
              <p className="text-sm mt-1">{loadError}</p>
              <p className="text-xs mt-2">
                Make sure packages are installed: npm install @ffmpeg/ffmpeg
                @ffmpeg/util
              </p>
            </div>
          )}

          {isExporting && (
            <div className="p-3 bg-light-yellow border border-site-yellow rounded-md text-site-black">
              <p className="font-semibold text-center mb-2">
                ⚡ Trimming video...
              </p>
              <div className="w-full bg-medium-grey rounded-full h-3 overflow-hidden">
                <div
                  className="bg-site-yellow h-3 rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{ width: `${exportProgress}%` }}
                >
                  {exportProgress > 10 && (
                    <span className="text-xs text-site-black font-semibold">
                      {exportProgress}%
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-center mt-2">
                Processing... {exportProgress}%
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
